import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { lookupExercise, ExerciseInfo } from '@/lib/api/exerciseDb';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Target, Dumbbell, Activity, ChevronRight, AlertTriangle, Flag, VideoOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface ExerciseGifModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

export function ExerciseGifModal({ isOpen, onClose, exerciseName }: ExerciseGifModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exercise, setExercise] = useState<ExerciseInfo | null>(null);
  const [alternatives, setAlternatives] = useState<ExerciseInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseInfo | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [reporting, setReporting] = useState(false);

  // Fetch exercise data
  useEffect(() => {
    if (isOpen && exerciseName) {
      setLoading(true);
      setError(null);
      setSelectedExercise(null);
      setImageUrl(null);
      
      lookupExercise(exerciseName).then((result) => {
        if (result.success && result.exercise) {
          setExercise(result.exercise);
          setAlternatives(result.alternatives || []);
          setSelectedExercise(result.exercise);
        } else {
          setError(result.error || 'Exercise not found');
        }
        setLoading(false);
      });
    }
  }, [isOpen, exerciseName]);

  // Fetch image when selected exercise changes (skip fallback exercises)
  useEffect(() => {
    if (selectedExercise?.id) {
      // Skip image fetch for fallback exercises - they don't have real API IDs
      if (selectedExercise.id.startsWith('fallback-')) {
        setImageUrl(null);
        setImageLoading(false);
        return;
      }
      
      setImageLoading(true);
      setImageUrl(null);
      
      supabase.functions.invoke('exercise-image', {
        body: { exerciseId: selectedExercise.id }
      }).then(({ data, error }) => {
        if (data?.success && data?.imageData) {
          setImageUrl(data.imageData);
        }
        setImageLoading(false);
      });
    }
  }, [selectedExercise?.id]);

  const displayExercise = selectedExercise || exercise;
  
  // Check if the returned exercise name significantly differs from what was searched
  const normalizedSearch = exerciseName.toLowerCase().replace(/[^a-z]/g, '');
  const normalizedResult = displayExercise?.name?.toLowerCase().replace(/[^a-z]/g, '') || '';
  const isApproximateMatch = displayExercise && !normalizedResult.includes(normalizedSearch) && !normalizedSearch.includes(normalizedResult);

  const handleReportMismatch = async () => {
    if (!user) {
      toast.error('Please sign in to report issues');
      return;
    }
    
    setReporting(true);
    try {
      const { error: feedbackError } = await supabase.from('feedback').insert({
        user_id: user.id,
        type: 'exercise_mismatch',
        message: `Searched for: "${exerciseName}"\nShowed: "${displayExercise?.name || 'Unknown'}"\nEquipment: ${displayExercise?.equipment || 'Unknown'}`,
      });
      
      if (feedbackError) throw feedbackError;
      toast.success('Thanks for reporting! We\'ll look into this.');
    } catch (err) {
      console.error('Failed to report:', err);
      toast.error('Failed to submit report');
    } finally {
      setReporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-background border-border pb-8 z-[60]">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-foreground">
            {exerciseName}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="mt-3 text-sm text-muted-foreground">Loading exercise...</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/30 flex items-center justify-center">
              <Dumbbell className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-foreground font-medium mb-2 capitalize">{exerciseName}</p>
            <p className="text-muted-foreground text-sm mb-4">
              {error.includes('API') 
                ? 'The exercise database is temporarily unavailable'
                : 'Details not available for this exercise'}
            </p>
            
            {/* General form tips when exercise not found */}
            <div className="text-left space-y-2 p-3 rounded-lg bg-secondary/20 mx-2">
              <p className="text-xs font-medium text-muted-foreground">General Form Tips:</p>
              <ul className="space-y-1 text-xs text-foreground/70">
                <li>• Maintain proper posture throughout</li>
                <li>• Control the movement, avoid using momentum</li>
                <li>• Breathe steadily - exhale on exertion</li>
                <li>• Start with lighter weight to master form</li>
              </ul>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleReportMismatch}
              disabled={reporting || !user}
              className="mt-4 text-xs"
            >
              <Flag className="w-3 h-3 mr-1" />
              {reporting ? 'Sending...' : 'Request this exercise'}
            </Button>
          </div>
        )}

        {displayExercise && !loading && (
          <div className="space-y-4">
            {/* Mismatch Warning */}
            {isApproximateMatch && (
              <div className="flex items-start justify-between gap-2 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-200">
                    Showing closest match. Verify this is correct before performing.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReportMismatch}
                  disabled={reporting}
                  className="h-6 px-2 text-xs text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20"
                >
                  <Flag className="w-3 h-3 mr-1" />
                  {reporting ? '...' : 'Report'}
                </Button>
              </div>
            )}

            {/* GIF */}
            <div className="rounded-lg overflow-hidden bg-secondary/30 min-h-[200px] flex items-center justify-center">
              {imageLoading ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt={displayExercise.name}
                  className="w-full h-auto"
                />
              ) : (
              <div className="flex flex-col items-center gap-3 py-8 px-4">
                  <VideoOff className="w-12 h-12 text-muted-foreground/50" />
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">No demo GIF available</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">See instructions below for guidance</p>
                  </div>
                </div>
              )}
            </div>

            {/* Exercise Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground capitalize">
                {displayExercise.name}
              </h3>

              {/* Muscle Groups */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10">
                  <Target className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Target</p>
                    <p className="text-sm font-medium text-foreground capitalize">
                      {displayExercise.target}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30">
                  <Activity className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-xs text-muted-foreground">Body Part</p>
                    <p className="text-sm font-medium text-foreground capitalize">
                      {displayExercise.bodyPart}
                    </p>
                  </div>
                </div>
              </div>

              {/* Equipment */}
              <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/20">
                <Dumbbell className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-foreground capitalize">
                  {displayExercise.equipment}
                </span>
              </div>

              {/* Secondary Muscles */}
              {displayExercise.secondaryMuscles && displayExercise.secondaryMuscles.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Also works:</p>
                  <div className="flex flex-wrap gap-1">
                    {displayExercise.secondaryMuscles.map((muscle, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent-foreground capitalize"
                      >
                        {muscle}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions */}
              {displayExercise.instructions && displayExercise.instructions.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground">Instructions</p>
                  <ol className="space-y-1.5">
                    {displayExercise.instructions.slice(0, 4).map((instruction, i) => (
                      <li key={i} className="flex gap-2 text-xs text-foreground/80">
                        <span className="text-primary font-medium">{i + 1}.</span>
                        <span>{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            {/* Alternatives */}
            {alternatives.length > 0 && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Similar exercises</p>
                <div className="space-y-1">
                  {alternatives.map((alt) => (
                    <button
                      key={alt.id}
                      onClick={() => setSelectedExercise(alt)}
                      className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                        selectedExercise?.id === alt.id
                          ? 'bg-primary/20 border border-primary/30'
                          : 'bg-secondary/20 hover:bg-secondary/40'
                      }`}
                    >
                      <span className="text-sm text-foreground capitalize truncate">
                        {alt.name}
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Liability Disclaimer */}
            <div className="pt-3 border-t border-border">
              <p className="text-[10px] text-muted-foreground/60 text-center leading-tight">
                Exercise at your own risk. Consult a physician before starting any fitness program. 
                We are not liable for any injuries that may occur.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
